import { useEffect, useState } from 'react';
import Loader from '../../common/components/Loader.tsx';
import { useNavigate } from 'react-router-dom';
import { NavigationRoutes } from '../../application/router/routes.ts';
import {
    InputAdornment,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeviationService from './services/deviationService.ts';
import { parseTransactionTypeToString } from '../../common/utils/parseTransactionTypeToString.ts';

const DeviationsOverview = () => {
    const [deviations, setDeviations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [pageNumbers, setPageNumbers] = useState({});
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [filter, setFilter] = useState<'active' | 'inactive'>('active'); // State for filter
    const navigate = useNavigate();
    const deviationService = new DeviationService();

    useEffect(() => {
        const fetchDeviations = async () => {
            setIsLoading(true);
            try {
                const response = await deviationService.getDeviations(filter);
                setDeviations(response);
                setPageNumbers({});
            } catch (error) {
                setError('Kunde inte hämta avvikelser. Försök igen.');
                console.error('Error fetching deviations:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDeviations();
    }, [filter]);

    const groupByStorageLocation = (deviations) => {
        return deviations.reduce((groups, deviation) => {
            const location = deviation.transactionRecord.storageLocation.name;
            if (!groups[location]) {
                groups[location] = [];
            }
            groups[location].push(deviation);
            return groups;
        }, {});
    };

    const filteredDeviations = deviations.filter((deviation) => {
        return (
            deviation.transactionRecord?.storageLocation.name
                .toLowerCase()
                .includes(searchQuery.toLowerCase()) ||
            deviation.transactionRecord?.username
                .toLowerCase()
                .includes(searchQuery.toLowerCase()) ||
            deviation.transactionRecord?.drug.drugName
                .toLowerCase()
                .includes(searchQuery.toLowerCase())
        );
    });

    const groupedDeviations = groupByStorageLocation(filteredDeviations);

    const handlePageChange = (location, newPage) => {
        setPageNumbers((prev) => ({ ...prev, [location]: newPage }));
    };

    const handleRowsPerPageChange = (event) => {
        const value = parseInt(event.target.value, 10);
        setRowsPerPage(value);
        setPageNumbers({});
    };

    if (isLoading) {
        return <Loader />;
    }

    if (error) {
        return <Typography color="error">{error}</Typography>;
    }

    return (
        <div className="p-3">
            <Typography
                variant="h4"
                fontWeight="bold"
                gutterBottom
                align="center"
                sx={{ width: '100%', mb: 3 }}
            >
                Avvikelser
            </Typography>
            <div className="mb-3">
                <div className="flex gap-2 flex-wrap">
                    <TextField
                        variant="outlined"
                        placeholder="Sök avvikelse..."
                        value={searchQuery}
                        size="small"
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Select
                        value={filter}
                        onChange={(e) =>
                            setFilter(e.target.value as 'active' | 'inactive')
                        }
                        variant="outlined"
                        size="small"
                    >
                        <MenuItem value="active">Ej hanterade</MenuItem>
                        <MenuItem value="inactive">Klarmarkerade</MenuItem>
                    </Select>
                </div>
            </div>
            {Object.keys(groupedDeviations).map((location) => {
                const page = pageNumbers[location] || 0;
                const deviationsForLocation = groupedDeviations[location];
                const paginatedDeviations = deviationsForLocation.slice(
                    page * rowsPerPage,
                    (page + 1) * rowsPerPage
                );

                return (
                    <div className="mt-6" key={location}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            {location}
                        </Typography>
                        <TableContainer component={Paper}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Läkemedel</TableCell>
                                        <TableCell>Skapad</TableCell>
                                        <TableCell>Avvikelse</TableCell>
                                        <TableCell>Typ av händelse</TableCell>
                                        <TableCell>Signatur</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedDeviations.map((deviation) => (
                                        <TableRow
                                            key={deviation.id}
                                            className="odd:bg-gray-100 hover:cursor-pointer"
                                            onClick={() =>
                                                navigate(
                                                    NavigationRoutes.deviationView +
                                                        `/${deviation.id}`
                                                )
                                            }
                                        >
                                            <TableCell>
                                                {
                                                    deviation.transactionRecord
                                                        .drug.drugName
                                                }
                                            </TableCell>
                                            <TableCell>
                                                {new Date(
                                                    deviation.transactionRecord
                                                        .createdOn as string
                                                ).toLocaleString('sv-se')}
                                            </TableCell>
                                            <TableCell>
                                                {
                                                    deviation.transactionRecord
                                                        .quantityMismatch
                                                }
                                            </TableCell>
                                            <TableCell>
                                                {parseTransactionTypeToString(
                                                    deviation.transactionRecord
                                                        .transactionType
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {
                                                    deviation.transactionRecord
                                                        .username
                                                }
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={deviationsForLocation.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={(_event: unknown, newPage: number) =>
                                handlePageChange(location, newPage)
                            }
                            onRowsPerPageChange={handleRowsPerPageChange}
                            rowsPerPageOptions={[5, 10, 25]}
                            labelRowsPerPage="Antal poster:"
                            labelDisplayedRows={({ from, to, count }) =>
                                `${from}-${to} av ${count !== -1 ? count : `mer än ${to}`}`
                            }
                        />
                    </div>
                );
            })}
        </div>
    );
};

export default DeviationsOverview;
